import { useEffect, useMemo, useState } from "react";
import { Button, Card, Title, Text } from "@tremor/react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  createRumAlertRule,
  createRumProject,
  deleteRumAlertRule,
  deleteRumProject,
  fetchRumAlertRules,
  fetchRumProjectSessions,
  fetchRumProjectSummary,
  fetchRumProjectTrend,
  fetchRumProjects,
  patchRumAlertRule,
  updateRumProject,
} from "../api/rum.js";

// Google CWV thresholds — assessed against p75 per Web Vitals spec.
const CWV_THRESHOLDS = {
  lcp_ms:  { good: 2500, ni: 4000 },
  fcp_ms:  { good: 1800, ni: 3000 },
  ttfb_ms: { good: 800,  ni: 1800 },
  inp_ms:  { good: 200,  ni: 500  },
  cls:     { good: 0.1,  ni: 0.25 },
};

function cwvRating(metric, value) {
  const t = CWV_THRESHOLDS[metric];
  if (!t || value == null) return null;
  if (value <= t.good) return "good";
  if (value <= t.ni)   return "ni";
  return "poor";
}

const CWV_BADGE = {
  good: "bg-green-100 text-green-700",
  ni:   "bg-yellow-100 text-yellow-700",
  poor: "bg-red-100 text-red-700",
};
const CWV_LABEL = { good: "Good", ni: "Needs improvement", poor: "Poor" };

function formatMetric(metric, p) {
  if (p == null) return "—";
  if (metric === "cls") return p.toFixed(3);
  return `${Math.round(p)} ms`;
}

function MetricRow({ label, metric, stats }) {
  if (!stats) return null;
  const rating = cwvRating(metric, stats.p75);
  return (
    <tr className="border-t border-gray-100 first:border-t-0">
      <td className="py-1.5 pr-3 font-medium text-gray-700">{label}</td>
      <td className="py-1.5 pr-3 text-right text-gray-600">
        {formatMetric(metric, stats.p50)}
      </td>
      <td className="py-1.5 pr-3 text-right text-gray-900 font-semibold">
        {formatMetric(metric, stats.p75)}
        {rating && (
          <span className={`ml-1.5 rounded px-1 py-0.5 text-[10px] font-medium ${CWV_BADGE[rating]}`}>
            {CWV_LABEL[rating]}
          </span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-right text-gray-600">
        {formatMetric(metric, stats.p95)}
      </td>
      <td className="py-1.5 text-right text-xs text-gray-400">
        n={stats.sample_size}
      </td>
    </tr>
  );
}

function ProjectSettingsPanel({ project, onChange }) {
  const [open, setOpen] = useState(false);
  const [origins, setOrigins] = useState((project.allowed_origins || []).join("\n"));
  const [sampleRate, setSampleRate] = useState(String(project.sample_rate ?? 1));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const updated = await updateRumProject(project.id, {
        allowed_origins: origins.split("\n").map((s) => s.trim()).filter(Boolean),
        sample_rate: parseFloat(sampleRate) || 1,
      });
      onChange(updated);
      toast.success("Settings saved");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="mt-2 text-xs text-gray-400 hover:text-gray-600 hover:underline"
        onClick={() => { setOrigins((project.allowed_origins || []).join("\n")); setSampleRate(String(project.sample_rate ?? 1)); setOpen(true); }}
      >
        Project settings (origins, sample rate)
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Project settings</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Allowed origins (one per line)</span>
          <textarea
            rows={3}
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder="https://mystore.com"
            className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Sample rate (0–1)</span>
          <input
            type="number"
            min="0.01"
            max="1"
            step="0.01"
            value={sampleRate}
            onChange={(e) => setSampleRate(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
          <p className="mt-1 text-[10px] text-gray-400">1 = collect every pageview, 0.1 = 10% sample</p>
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="xs" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        <Button variant="secondary" size="xs" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function SnippetBlock({ siteId }) {
  const [tab, setTab] = useState("standard");
  const base = useMemo(
    () => window.location.origin.replace(/^https?:\/\/([^.]+\.)/, "https://rum."),
    []
  );
  const stdSnippet = useMemo(
    () =>
      `<script src="${base}/rum.js" data-site-id="${siteId}" async></script>`,
    [base, siteId]
  );
  const shopifySnippet = useMemo(
    () =>
      `<!-- Standard RUM -->\n<script src="${base}/rum.js" data-site-id="${siteId}" async></script>\n<!-- Shopify cart + checkout events -->\n<script src="${base}/rum-shopify.js" async></script>`,
    [base, siteId]
  );
  const wooSnippet = useMemo(
    () =>
      `<!-- Standard RUM -->\n<script src="${base}/rum.js" data-site-id="${siteId}" async></script>\n<!-- WooCommerce add_to_cart, checkout + order events -->\n<script src="${base}/rum-woocommerce.js" async></script>`,
    [base, siteId]
  );
  const trackSnippet = `// After the RUM script loads, call window.monitero.track()
// to record custom events with optional properties.

// Example: track a button click
document.querySelector("#upgrade-btn").addEventListener("click", () => {
  window.monitero.track("upgrade_clicked", { plan: "pro" });
});

// Example: track a form submission
window.monitero.track("checkout_started", {
  cart_value: 49.99,
  item_count: 3,
});

// Signature:
// window.monitero.track(name: string, props?: Record<string, string|number|boolean>)
// - name: up to 60 chars
// - props: up to 5 keys, values truncated to 200 chars`;

  const activeSnippet = tab === "track" ? trackSnippet : tab === "shopify" ? shopifySnippet : tab === "woocommerce" ? wooSnippet : stdSnippet;

  const TAB_LABELS = { standard: "Standard", shopify: "Shopify", woocommerce: "WooCommerce", track: "Custom events" };
  const TAB_HINTS = {
    shopify: "Auto-wires add_to_cart, checkout_started, purchase_completed via Web Pixels",
    woocommerce: "Auto-wires add_to_cart (AJAX + jQuery), checkout_started, purchase_completed",
    track: "Custom events appear in the Dashboard under Top custom events",
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Embed snippet
        </p>
        <div className="flex gap-1 rounded border border-gray-200 bg-white p-0.5">
          {["standard", "shopify", "woocommerce", "track"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded px-2 py-0.5 text-xs ${
                tab === t ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-white p-2 font-mono text-xs text-gray-800">
        {activeSnippet}
      </pre>
      <div className="mt-2 flex items-center justify-between">
        {tab !== "track" ? (
          <button
            type="button"
            className="text-xs text-teal-700 hover:underline"
            onClick={() => {
              navigator.clipboard.writeText(activeSnippet);
              toast.success("Copied snippet to clipboard");
            }}
          >
            Copy
          </button>
        ) : <span />}
        {TAB_HINTS[tab] && (
          <p className="text-xs text-gray-400">{TAB_HINTS[tab]}</p>
        )}
      </div>
    </div>
  );
}

function CreateProjectModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [sampleRate, setSampleRate] = useState("1");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const project = await createRumProject({
        name: name.trim(),
        allowed_origins: origins
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        sample_rate: parseFloat(sampleRate) || 1,
      });
      toast.success("Project created");
      onCreated(project);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
      >
        <h2 className="mb-3 text-lg font-semibold">New RUM project</h2>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            placeholder="Marketing site"
            required
          />
        </label>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            Allowed origins (one per line)
          </span>
          <textarea
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            rows={3}
            className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
            placeholder={"https://example.com\nhttps://*.example.com"}
          />
          <span className="mt-1 block text-xs text-gray-500">
            Only events from these origins will be accepted. Use{" "}
            <code>*.</code> for subdomain wildcards.
          </span>
        </label>
        <label className="mb-4 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            Sample rate (0–1)
          </span>
          <input
            value={sampleRate}
            onChange={(e) => setSampleRate(e.target.value)}
            className="w-32 rounded border border-gray-300 px-2 py-1.5 text-sm"
            placeholder="1"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} type="button" disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}

const _TREND_METRICS = {
  lcp: { label: "LCP", p75Key: "p75_lcp_ms", p50Key: "p50_lcp_ms", color: "#0D9488", altColor: "#6366f1" },
  fcp: { label: "FCP", p75Key: "p75_fcp_ms", p50Key: null, color: "#f59e0b", altColor: null },
  ttfb: { label: "TTFB", p75Key: "p75_ttfb_ms", p50Key: null, color: "#8b5cf6", altColor: null },
};

function LcpTrendChart({ projectId }) {
  const [trend, setTrend] = useState(null);
  const [days, setDays] = useState(7);
  const [metric, setMetric] = useState("lcp");

  useEffect(() => {
    fetchRumProjectTrend(projectId, days)
      .then(setTrend)
      .catch(() => setTrend(null));
  }, [projectId, days]);

  if (!trend || !trend.buckets.length) return null;

  const m = _TREND_METRICS[metric];
  const data = trend.buckets.map((b) => ({
    day: b.day.slice(5),
    p75: b[m.p75Key],
    p50: m.p50Key ? b[m.p50Key] : undefined,
    views: b.pageviews,
    sessions: b.sessions,
    errors: b.total_errors || 0,
  }));
  const hasErrors = data.some((d) => d.errors > 0);

  const controls = (
    <div className="flex items-center gap-2">
      <div className="flex gap-1 rounded border border-gray-200 bg-white p-0.5">
        {Object.entries(_TREND_METRICS).map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => setMetric(k)}
            className={`rounded px-2 py-0.5 text-xs ${metric === k ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded px-2 py-0.5 text-xs ${days === d ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
          >
            {d}d
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mt-4 space-y-3">
      {/* CWV metric trend */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {m.label} trend (p75{m.p50Key ? " / p50" : ""})
          </p>
          {controls}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 32 }}>
              <defs>
                <linearGradient id="cwv75" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={m.color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                </linearGradient>
                {m.altColor && (
                  <linearGradient id="cwv50" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={m.altColor} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={m.altColor} stopOpacity={0} />
                  </linearGradient>
                )}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit=" ms" />
              <Tooltip
                formatter={(v, name) => [v != null ? `${Math.round(v)} ms` : "—", name]}
                labelStyle={{ fontSize: 11 }}
                contentStyle={{ fontSize: 11 }}
              />
              <Area
                type="monotone"
                dataKey="p75"
                name={`p75 ${m.label}`}
                stroke={m.color}
                fill="url(#cwv75)"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              {m.p50Key && (
                <Area
                  type="monotone"
                  dataKey="p50"
                  name={`p50 ${m.label}`}
                  stroke={m.altColor}
                  fill="url(#cwv50)"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  dot={false}
                  connectNulls
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Traffic trend */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Traffic (pageviews / sessions{hasErrors ? " / JS errors" : ""})
        </p>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 32 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                formatter={(v, name) => [v?.toLocaleString() ?? "—", name]}
                labelStyle={{ fontSize: 11 }}
                contentStyle={{ fontSize: 11 }}
              />
              <Bar dataKey="views" name="Pageviews" fill="#0D9488" opacity={0.8} radius={[2, 2, 0, 0]} />
              <Bar dataKey="sessions" name="Sessions" fill="#6366f1" opacity={0.6} radius={[2, 2, 0, 0]} />
              {hasErrors && (
                <Bar dataKey="errors" name="JS errors" fill="#ef4444" opacity={0.75} radius={[2, 2, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PathFilterInput({ pages, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value);
  const paths = pages.map((p) => p.page_path).filter(Boolean);
  const filtered = input ? paths.filter((p) => p.includes(input)) : paths;

  function commit(v) {
    setInput(v);
    onChange(v);
    setOpen(false);
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1">
        <input
          type="text"
          placeholder="Filter by path…"
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(input); if (e.key === "Escape") commit(""); }}
          className="w-36 text-xs outline-none"
        />
        {input && (
          <button type="button" onClick={() => commit("")} className="text-gray-400 hover:text-gray-600 text-xs leading-none">×</button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul className="absolute top-full left-0 z-20 mt-0.5 max-h-48 w-56 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
          {filtered.slice(0, 20).map((p) => (
            <li
              key={p}
              className="cursor-pointer px-2 py-1 text-xs hover:bg-teal-50 font-mono"
              onMouseDown={() => commit(p)}
            >
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProjectDetail({ project, onChange, onDelete }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [window, setWindow] = useState("24h");
  const [pathFilter, setPathFilter] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchRumProjectSummary(project.id, window, pathFilter || null)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [project.id, window, pathFilter]);

  async function saveName() {
    const name = nameDraft.trim();
    setEditingName(false);
    if (!name || name === project.name) return;
    try {
      const updated = await updateRumProject(project.id, { name });
      onChange(updated);
      toast.success("Project renamed");
    } catch {
      toast.error("Rename failed");
    }
  }

  async function toggleActive() {
    try {
      const updated = await updateRumProject(project.id, {
        is_active: !project.is_active,
      });
      onChange(updated);
      toast.success(updated.is_active ? "Project enabled" : "Project paused");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Update failed");
    }
  }

  async function remove() {
    if (!confirm(`Delete project "${project.name}"? Event history is also removed.`)) return;
    try {
      await deleteRumProject(project.id);
      onDelete();
      toast.success("Project deleted");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Delete failed");
    }
  }

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
              className="rounded border border-teal-400 px-2 py-0.5 text-base font-semibold outline-none"
            />
          ) : (
            <Title
              className="cursor-pointer hover:text-teal-700"
              title="Click to rename"
              onClick={() => { setNameDraft(project.name); setEditingName(true); }}
            >{project.name}</Title>
          )}
          <Text className="font-mono text-xs">{project.site_id}</Text>
        </div>
        <div className="flex items-center gap-2">
          <PathFilterInput
            pages={summary?.top_pages || []}
            value={pathFilter}
            onChange={setPathFilter}
          />
          <select
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="1h">Last hour</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
          </select>
          <Button variant="secondary" size="xs" onClick={toggleActive}>
            {project.is_active ? "Pause" : "Resume"}
          </Button>
          <Button variant="secondary" size="xs" onClick={remove}>
            Delete
          </Button>
        </div>
      </div>

      <SnippetBlock siteId={project.site_id} />
      <ProjectSettingsPanel project={project} onChange={onChange} />

      {summary && (
        <div className="mt-4 flex flex-wrap gap-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-center">
            <p className="text-xs text-gray-500">Pageviews</p>
            <p className="mt-0.5 text-lg font-semibold text-gray-900">
              {(summary.total_events ?? 0).toLocaleString()}
            </p>
            {summary.prev_period?.events_change_pct != null && (
              <p className={`text-[10px] ${summary.prev_period.events_change_pct >= 0 ? "text-green-600" : "text-red-500"}`}>
                {summary.prev_period.events_change_pct >= 0 ? "↑" : "↓"}{Math.abs(summary.prev_period.events_change_pct)}% vs prev
              </p>
            )}
          </div>
          {(summary.unique_sessions ?? 0) > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-center">
              <p className="text-xs text-gray-500">Sessions</p>
              <p className="mt-0.5 text-lg font-semibold text-gray-900">
                {summary.unique_sessions.toLocaleString()}
              </p>
              {summary.prev_period?.sessions_change_pct != null && (
                <p className={`text-[10px] ${summary.prev_period.sessions_change_pct >= 0 ? "text-green-600" : "text-red-500"}`}>
                  {summary.prev_period.sessions_change_pct >= 0 ? "↑" : "↓"}{Math.abs(summary.prev_period.sessions_change_pct)}% vs prev
                </p>
              )}
            </div>
          )}
          {(summary.unique_sessions ?? 0) > 0 && (summary.total_events ?? 0) > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-center">
              <p className="text-xs text-gray-500">Pages / session</p>
              <p className="mt-0.5 text-lg font-semibold text-gray-900">
                {(summary.total_events / summary.unique_sessions).toFixed(1)}
              </p>
            </div>
          )}
          {summary.bounce_rate != null && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-center">
              <p className="text-xs text-gray-500">Bounce rate</p>
              <p className={`mt-0.5 text-lg font-semibold ${summary.bounce_rate > 70 ? "text-red-600" : summary.bounce_rate > 50 ? "text-amber-600" : "text-gray-900"}`}>
                {summary.bounce_rate}%
              </p>
            </div>
          )}
          {(summary.total_errors ?? 0) > 0 && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-center">
              <p className="text-xs text-red-500">JS errors</p>
              <p className="mt-0.5 text-lg font-semibold text-red-700">
                {summary.total_errors.toLocaleString()}
              </p>
            </div>
          )}
          {summary.metrics?.lcp_ms?.p75 != null && (() => {
            const r = cwvRating("lcp_ms", summary.metrics.lcp_ms.p75);
            const borderCls = r === "good" ? "border-green-200 bg-green-50" : r === "ni" ? "border-yellow-200 bg-yellow-50" : "border-red-200 bg-red-50";
            const valCls = r === "good" ? "text-green-700" : r === "ni" ? "text-yellow-700" : "text-red-700";
            return (
              <div className={`rounded-lg border px-4 py-2.5 text-center ${borderCls}`}>
                <p className={`text-xs ${valCls} opacity-70`}>LCP p75</p>
                <p className={`mt-0.5 text-lg font-semibold ${valCls}`}>
                  {Math.round(summary.metrics.lcp_ms.p75)}ms
                </p>
              </div>
            );
          })()}
        </div>
      )}

      {summary?.cwv_pass_rates && Object.keys(summary.cwv_pass_rates).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {[
            { key: "lcp_ms", label: "LCP" },
            { key: "fcp_ms", label: "FCP" },
            { key: "ttfb_ms", label: "TTFB" },
            { key: "inp_ms", label: "INP" },
            { key: "cls", label: "CLS" },
          ].map(({ key, label }) => {
            const rate = summary.cwv_pass_rates[key];
            if (rate == null) return null;
            const color = rate >= 75 ? "bg-green-50 border-green-200 text-green-700"
              : rate >= 50 ? "bg-yellow-50 border-yellow-200 text-yellow-700"
              : "bg-red-50 border-red-200 text-red-700";
            return (
              <div key={key} className={`rounded-lg border px-3 py-1.5 text-center ${color}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label} good</p>
                <p className="text-base font-bold">{rate}%</p>
              </div>
            );
          })}
        </div>
      )}

      <LcpTrendChart projectId={project.id} />

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Core Web Vitals
        </p>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : summary?.total_events ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-1.5 pr-3">Metric</th>
                <th className="py-1.5 pr-3 text-right">p50</th>
                <th className="py-1.5 pr-3 text-right">p75</th>
                <th className="py-1.5 pr-3 text-right">p95</th>
                <th className="py-1.5 text-right">Samples</th>
              </tr>
            </thead>
            <tbody>
              <MetricRow label="TTFB" metric="ttfb_ms" stats={summary.metrics.ttfb_ms} />
              <MetricRow label="FCP" metric="fcp_ms" stats={summary.metrics.fcp_ms} />
              <MetricRow label="LCP" metric="lcp_ms" stats={summary.metrics.lcp_ms} />
              <MetricRow label="CLS" metric="cls" stats={summary.metrics.cls} />
              <MetricRow label="INP" metric="inp_ms" stats={summary.metrics.inp_ms} />
              <MetricRow label="Load" metric="load_ms" stats={summary.metrics.load_ms} />
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-400">
            No events received yet. Embed the snippet on a page that matches one of
            the allowed origins and reload it.
          </p>
        )}
      </div>

      {summary?.by_device && Object.keys(summary.by_device).length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            LCP by device (p75)
          </p>
          <div className="flex flex-wrap gap-3">
            {(() => {
              const deviceTotals = Object.fromEntries(
                (summary.breakdown?.device_type || []).map((d) => [d.value, d.count])
              );
              const totalViews = Object.values(deviceTotals).reduce((s, c) => s + c, 0);
              return Object.entries(summary.by_device).map(([device, stats]) => {
                const views = deviceTotals[device] ?? stats.count;
                const pct = totalViews > 0 ? Math.round(100 * views / totalViews) : null;
                return (
                  <div
                    key={device}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center"
                  >
                    <p className="capitalize text-xs text-gray-500">
                      {device}{pct != null ? ` · ${pct}%` : ""}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-800">
                      {Math.round(stats.p75_lcp_ms)} ms
                    </p>
                    <p className="text-xs text-gray-400">n={views.toLocaleString()}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {summary?.custom_events?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Custom events
          </p>
          <ul className="space-y-1 text-sm">
            {summary.custom_events.map((e) => (
              <li key={e.name} className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-gray-700">{e.name}</span>
                <span className="text-xs text-gray-500">{e.count.toLocaleString()}×</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary?.breakdown && (
        summary.breakdown.browser?.length > 0 ||
        summary.breakdown.country?.length > 0 ||
        summary.breakdown.referrer?.length > 0 ||
        summary.breakdown.device_type?.length > 0
      ) && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {summary.breakdown.device_type?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Devices
              </p>
              <ul className="space-y-1 text-sm">
                {summary.breakdown.device_type.map((d) => {
                  const total = summary.breakdown.device_type.reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round(100 * d.count / total) : 0;
                  return (
                    <li key={d.value} className="flex items-center gap-2">
                      <span className="w-16 truncate text-xs text-gray-700 capitalize">{d.value || "Unknown"}</span>
                      <div className="h-1.5 flex-1 rounded bg-gray-100">
                        <div className="h-1.5 rounded bg-sky-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs text-gray-400">{pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {summary.breakdown.browser?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Browsers
              </p>
              <ul className="space-y-1 text-sm">
                {summary.breakdown.browser.map((b) => {
                  const total = summary.breakdown.browser.reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round(100 * b.count / total) : 0;
                  return (
                    <li key={b.value} className="flex items-center gap-2">
                      <span className="w-24 truncate text-xs text-gray-700">{b.value || "Unknown"}</span>
                      <div className="h-1.5 flex-1 rounded bg-gray-100">
                        <div className="h-1.5 rounded bg-teal-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs text-gray-400">{pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {summary.breakdown.country?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Countries
              </p>
              <ul className="space-y-1 text-sm">
                {summary.breakdown.country.map((c) => {
                  const total = summary.breakdown.country.reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round(100 * c.count / total) : 0;
                  return (
                    <li key={c.value} className="flex items-center gap-2">
                      <span className="w-8 font-mono text-xs font-medium text-gray-700">{c.value}</span>
                      <div className="h-1.5 flex-1 rounded bg-gray-100">
                        <div className="h-1.5 rounded bg-indigo-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs text-gray-400">{pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {summary.breakdown.referrer?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Referrers
              </p>
              <ul className="space-y-1 text-sm">
                {summary.breakdown.referrer.map((r) => {
                  const total = summary.breakdown.referrer.reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round(100 * r.count / total) : 0;
                  return (
                    <li key={r.value} className="flex items-center gap-2">
                      <span className="w-28 truncate text-xs text-gray-700" title={r.value}>{r.value || "Direct"}</span>
                      <div className="h-1.5 flex-1 rounded bg-gray-100">
                        <div className="h-1.5 rounded bg-violet-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs text-gray-400">{pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {summary?.top_pages?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Top pages by traffic
          </p>
          <ul className="space-y-1">
            {summary.top_pages.map((p) => {
              const max = summary.top_pages[0].pageviews;
              const pct = max > 0 ? Math.round(100 * p.pageviews / max) : 0;
              return (
                <li key={p.page_path} className="flex items-center gap-2">
                  <span className="w-40 truncate font-mono text-xs text-gray-700" title={p.page_path}>
                    {p.page_path}
                  </span>
                  <div className="h-1.5 flex-1 rounded bg-gray-100">
                    <div className="h-1.5 rounded bg-teal-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-12 text-right text-xs text-gray-400">
                    {p.pageviews.toLocaleString()}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {summary?.slowest_pages?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Slowest pages (p75 LCP)
          </p>
          <ul className="space-y-2 text-sm">
            {summary.slowest_pages.map((p) => (
              <li key={p.page_path}>
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="truncate font-mono text-xs text-gray-700" title={p.page_path}>
                    {p.page_path}
                  </span>
                  <span className="whitespace-nowrap text-xs text-gray-500 shrink-0 flex items-center gap-2">
                    <span title="p75 LCP">LCP {Math.round(p.p75_lcp_ms)}ms</span>
                    {p.p75_fcp_ms != null && <span className="text-amber-600" title="p75 FCP">FCP {Math.round(p.p75_fcp_ms)}ms</span>}
                    {p.p75_ttfb_ms != null && <span className="text-violet-600" title="p75 TTFB">TTFB {Math.round(p.p75_ttfb_ms)}ms</span>}
                    <span className="text-gray-400">n={p.sample_size}</span>
                  </span>
                </div>
                {p.lcp_good_pct != null && (
                  <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100" title={`Good ${p.lcp_good_pct}% · NI ${p.lcp_ni_pct}% · Poor ${p.lcp_poor_pct}%`}>
                    <div className="bg-green-500" style={{ width: `${p.lcp_good_pct}%` }} />
                    <div className="bg-yellow-400" style={{ width: `${p.lcp_ni_pct}%` }} />
                    <div className="bg-red-500" style={{ width: `${p.lcp_poor_pct}%` }} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(summary?.top_errors?.length > 0 || summary?.error_pages?.length > 0) && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            JS errors
          </p>
          {summary.top_errors?.length > 0 && (
            <ul className="mb-3 space-y-1">
              {summary.top_errors.map((e, i) => (
                <li key={i} className="flex items-start gap-2 rounded bg-red-50 px-2.5 py-1.5">
                  <span className="mt-0.5 shrink-0 text-xs font-bold text-red-500">{e.count}×</span>
                  <span className="break-all font-mono text-xs text-red-800">{e.message}</span>
                </li>
              ))}
            </ul>
          )}
          {summary.error_pages?.length > 0 && (
            <ul className="space-y-1">
              {summary.error_pages.map((p) => (
                <li key={p.page_path} className="flex items-center justify-between gap-2">
                  <span className="truncate font-mono text-xs text-gray-500" title={p.page_path}>
                    {p.page_path}
                  </span>
                  <span className="text-xs text-red-600">{p.errors} errors</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {summary?.failed_resources?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Failed resources
          </p>
          <ul className="space-y-1">
            {summary.failed_resources.map((r, i) => (
              <li key={i} className="flex items-start gap-2 rounded bg-amber-50 px-2.5 py-1.5">
                <span className="mt-0.5 shrink-0 text-xs font-bold text-amber-600">{r.count}×</span>
                <span className="break-all font-mono text-xs text-amber-900">{r.url}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SessionsPanel projectId={project.id} />
      <AlertRulesPanel projectId={project.id} />
    </Card>
  );
}

function SessionsPanel({ projectId }) {
  const [sessions, setSessions] = useState(null);
  const [days, setDays] = useState(7);
  const [open, setOpen] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    setSessions(null);
    fetchRumProjectSessions(projectId, days)
      .then(setSessions)
      .catch(() => setSessions([]));
  }, [projectId, days, open]);

  function fmtLcp(ms) {
    if (ms == null) return "—";
    const rounded = Math.round(ms) + "ms";
    if (ms < 2500) return <span className="text-green-700">{rounded}</span>;
    if (ms < 4000) return <span className="text-amber-600">{rounded}</span>;
    return <span className="text-red-600">{rounded}</span>;
  }

  function relTime(iso) {
    const secs = (Date.now() - new Date(iso).getTime()) / 1000;
    if (secs < 90) return "just now";
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Recent sessions
        </p>
        <span className="text-xs text-gray-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-2">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <div className="flex gap-1">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`rounded px-2 py-0.5 text-xs ${days === d ? "bg-teal-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {["all", "desktop", "mobile", "tablet"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setDeviceFilter(f)}
                  className={`rounded px-2 py-0.5 text-xs capitalize ${deviceFilter === f ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          {sessions === null ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-gray-400">No sessions in this window.</p>
          ) : (() => {
            const filtered = deviceFilter === "all"
              ? sessions
              : sessions.filter((s) => s.device_type === deviceFilter);
            return (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[10px] uppercase text-gray-400">
                    <th className="pb-1.5 pr-3">Entry</th>
                    <th className="pb-1.5 pr-3 text-right">Pages</th>
                    <th className="pb-1.5 pr-3 text-right">LCP p75</th>
                    <th className="pb-1.5 pr-3 text-right">Errors</th>
                    <th className="pb-1.5 pr-3">Device</th>
                    <th className="pb-1.5 text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} className="py-2 text-center text-gray-400">No {deviceFilter} sessions in this window.</td></tr>
                  ) : filtered.map((s) => (
                    <tr key={s.session_id} className="border-b border-gray-50">
                      <td className="max-w-[160px] truncate py-1 pr-3 font-mono text-gray-600" title={s.entry_path}>
                        {s.entry_path || "/"}
                      </td>
                      <td className="py-1 pr-3 text-right text-gray-700">{s.pages}</td>
                      <td className="py-1 pr-3 text-right font-medium">{fmtLcp(s.p75_lcp_ms)}</td>
                      <td className="py-1 pr-3 text-right">
                        {s.errors > 0 ? (
                          <span className="font-medium text-red-600">{s.errors}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-1 pr-3 capitalize text-gray-500">{s.device_type || "—"}</td>
                      <td className="py-1 text-right text-gray-400">{relTime(s.first_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

const METRIC_OPTIONS = [
  { value: "lcp_ms",      label: "LCP",             hint: "Good ≤2500ms" },
  { value: "fcp_ms",      label: "FCP",             hint: "Good ≤1800ms" },
  { value: "ttfb_ms",     label: "TTFB",            hint: "Good ≤800ms" },
  { value: "inp_ms",      label: "INP",             hint: "Good ≤200ms" },
  { value: "cls",         label: "CLS",             hint: "Good ≤0.1" },
  { value: "load_ms",     label: "Page load",       hint: "ms" },
  { value: "error_rate",  label: "JS error rate",   hint: "errors per pageview, e.g. 0.05" },
  { value: "bounce_rate", label: "Bounce rate",     hint: "% e.g. 70 for 70%" },
];

function AlertRulesPanel({ projectId }) {
  const [rules, setRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", metric: "lcp_ms", threshold: "", window_minutes: 60, channel_id: "", severity: "high" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRumAlertRules(projectId).then(setRules).catch(() => {});
    import("../api/channels.js").then((m) => m.listChannels()).then(setChannels).catch(() => {});
  }, [projectId]);

  async function save() {
    if (!form.name || !form.threshold) return;
    setSaving(true);
    try {
      const rule = await createRumAlertRule(projectId, {
        ...form,
        threshold: parseFloat(form.threshold),
        window_minutes: parseInt(form.window_minutes) || 60,
        channel_id: form.channel_id || null,
      });
      setRules((r) => [...r, rule]);
      setShowForm(false);
      setForm({ name: "", metric: "lcp_ms", threshold: "", window_minutes: 60, channel_id: "", severity: "high" });
      toast.success("Alert rule created");
    } catch {
      toast.error("Failed to create alert rule");
    } finally {
      setSaving(false);
    }
  }

  async function toggle(id, current) {
    try {
      const updated = await patchRumAlertRule(id, { is_active: !current });
      setRules((r) => r.map((x) => (x.id === id ? updated : x)));
    } catch {
      toast.error("Failed to update rule");
    }
  }

  async function remove(id) {
    try {
      await deleteRumAlertRule(id);
      setRules((r) => r.filter((x) => x.id !== id));
      toast.success("Rule deleted");
    } catch {
      toast.error("Failed to delete");
    }
  }

  return (
    <div className="mt-5 border-t border-gray-100 pt-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Alert rules</p>
        <Button size="xs" variant="secondary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "+ Add rule"}
        </Button>
      </div>

      {showForm && (
        <div className="mb-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <input
            className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
            placeholder="Rule name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <div className="flex gap-2">
            <select
              className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
              value={form.metric}
              onChange={(e) => setForm((f) => ({ ...f, metric: e.target.value }))}
            >
              {METRIC_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} ({o.hint})</option>
              ))}
            </select>
            <input
              className="w-28 rounded border border-gray-300 px-2 py-1 text-xs"
              type="number"
              placeholder="Threshold"
              value={form.threshold}
              onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Window:</span>
            <select
              className="rounded border border-gray-300 px-2 py-1 text-xs"
              value={form.window_minutes}
              onChange={(e) => setForm((f) => ({ ...f, window_minutes: e.target.value }))}
            >
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>1 hour</option>
              <option value={360}>6 hours</option>
              <option value={1440}>24 hours</option>
            </select>
            {channels.length > 0 && (
              <select
                className="rounded border border-gray-300 px-2 py-1 text-xs"
                value={form.channel_id}
                onChange={(e) => setForm((f) => ({ ...f, channel_id: e.target.value }))}
              >
                <option value="">Incident only</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <select
              className="rounded border border-gray-300 px-2 py-1 text-xs"
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <Button size="xs" onClick={save} disabled={saving}>Save</Button>
          </div>
        </div>
      )}

      {rules.length === 0 && !showForm ? (
        <p className="text-xs text-gray-400">No alert rules. Add one to get notified when a CWV metric degrades.</p>
      ) : (
        <ul className="space-y-1">
          {rules.map((r) => (
            <RumAlertRuleRow
              key={r.id}
              rule={r}
              channels={channels}
              onToggle={() => toggle(r.id, r.is_active)}
              onDelete={() => remove(r.id)}
              onSave={async (patch) => {
                const updated = await patchRumAlertRule(r.id, patch);
                setRules((rs) => rs.map((x) => (x.id === r.id ? updated : x)));
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtWindow(minutes) {
  if (minutes >= 1440 && minutes % 1440 === 0) return `${minutes / 1440}d`;
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}m`;
}

function RumAlertRuleRow({ rule: r, channels = [], onToggle, onDelete, onSave }) {
  const [editThreshold, setEditThreshold] = useState(false);
  const [draft, setDraft] = useState(String(r.threshold));
  const metaLabel = METRIC_OPTIONS.find((o) => o.value === r.metric)?.label || r.metric;

  async function saveThreshold() {
    const val = parseFloat(draft);
    if (!isNaN(val) && val !== r.threshold) {
      try {
        await onSave({ threshold: val });
        toast.success("Threshold updated");
      } catch {
        toast.error("Update failed");
      }
    }
    setEditThreshold(false);
  }

  return (
    <li className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${r.is_active ? "border-gray-100 bg-white" : "border-gray-100 bg-gray-50 opacity-60"}`}>
      <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
        <span className="font-medium text-gray-800">{r.name}</span>
        {r.is_breaching && (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">Firing</span>
        )}
        <span className="text-gray-400">
          {metaLabel} {["error_rate", "bounce_rate"].includes(r.metric) ? ">" : "p75 >"}{" "}
        </span>
        {editThreshold ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveThreshold}
            onKeyDown={(e) => { if (e.key === "Enter") saveThreshold(); if (e.key === "Escape") setEditThreshold(false); }}
            className="w-16 rounded border border-teal-400 px-1 py-0.5 text-xs outline-none"
          />
        ) : (
          <button
            type="button"
            title="Click to edit"
            onClick={() => { setDraft(String(r.threshold)); setEditThreshold(true); }}
            className="rounded bg-gray-100 px-1 py-0.5 font-mono hover:bg-teal-50 hover:text-teal-700"
          >
            {r.threshold}
          </button>
        )}
        <span className="text-gray-400">over {fmtWindow(r.window_minutes)}</span>
        {r.channel_id && (
          <span className="text-teal-700 text-[11px]">
            → {channels.find((c) => c.id === r.channel_id)?.name ?? "notify"}
          </span>
        )}
        {r.severity && r.severity !== "high" && (
          <span className={`text-[10px] font-medium px-1 py-0.5 rounded ${
            r.severity === "critical" ? "bg-red-100 text-red-700"
            : r.severity === "medium" ? "bg-amber-100 text-amber-700"
            : "bg-gray-100 text-gray-500"
          }`}>{r.severity}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={onToggle}
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${r.is_active ? "text-teal-700 bg-teal-50 hover:bg-teal-100" : "text-gray-500 bg-gray-100 hover:bg-gray-200"}`}
        >
          {r.is_active ? "Active" : "Paused"}
        </button>
        <button onClick={onDelete} className="text-gray-400 hover:text-red-500">✕</button>
      </div>
    </li>
  );
}

export default function Rum() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    setLoading(true);
    fetchRumProjects()
      .then(setProjects)
      .catch((e) => toast.error(e?.response?.data?.error || "Load failed"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function onProjectChange(updated) {
    setProjects((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Title>Real User Monitoring</Title>
        <Button onClick={() => setShowCreate(true)}>New project</Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : projects.length === 0 ? (
        <Card>
          <Text>
            No RUM projects yet. Create one, embed the snippet on your site,
            and Core Web Vitals from real visitors will appear here.
          </Text>
        </Card>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => (
            <ProjectDetail
              key={p.id}
              project={p}
              onChange={onProjectChange}
              onDelete={load}
            />
          ))}
        </div>
      )}

      <CreateProjectModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(p) => setProjects((ps) => [...ps, p])}
      />
    </div>
  );
}
